"use client";

import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  BookOpenCheck,
  GraduationCap,
  ListChecks,
  Users,
  AlertTriangle,
  RefreshCw,
  Activity,
  KeyRound,
  MessageSquare,
  Calendar,
  Video,
  TrendingUp,
  Clock,
  Zap,
  ArrowRight,
  ChevronRight,
} from "lucide-react";
import { motion } from "framer-motion";
import clsx from "clsx";
import { AdminShell } from "@/components/admin-shell";
import {
  AccentDot,
  Bento,
  PageHero,
  Pill,
  Widget,
  accentHex,
  type WidgetAccent,
} from "@/components/Widget";
import { api } from "@/lib/api";
import { useScrollRevealAll, useCounter } from "@/hooks/useScrollReveal";

interface Stats {
  levels: number;
  lessons: number;
  exercises: number;
  lessonsEncodingPending: number;
  users: number;
}

interface AuditLog {
  id: string;
  userId: string | null;
  lessonId: string | null;
  action: "token_issued" | "key_fetched" | "auth_failed" | "segment_404";
  ipHash: string | null;
  uaFingerprint: string | null;
  sessionNonce: string | null;
  createdAt: string;
}

interface QuickAction {
  href: string;
  label: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
  accent: WidgetAccent;
  description: string;
}

const QUICK_ACTIONS: QuickAction[] = [
  { href: "/levels",     label: "Add a level",      icon: BookOpenCheck, accent: "yellow", description: "Create a new CEFR level" },
  { href: "/lessons",    label: "Upload a video",   icon: Video,         accent: "green",  description: "MP4 → HLS ladder" },
  { href: "/exercises",  label: "Create exercise",   icon: ListChecks,    accent: "blue",   description: "5 exercise types" },
  { href: "/bookings",   label: "Open bookings",    icon: Calendar,      accent: "orange", description: "Tutor slots" },
  { href: "/whatsapp",   label: "WhatsApp groups",  icon: MessageSquare, accent: "purple", description: "Level chat rooms" },
  { href: "/subscriptions", label: "Grant access",   icon: KeyRound,      accent: "red",    description: "Manual tier grants" },
];

function AnimatedCounter({ target }: { target: number }) {
  const { ref, value } = useCounter(target);
  return <span ref={ref}>{value.toLocaleString()}</span>;
}

function StatTile({
  title,
  value,
  hint,
  link,
  accent,
  icon: Icon,
}: {
  title: string;
  value: number | string;
  hint: string;
  link: string;
  accent: WidgetAccent;
  icon: React.ComponentType<{ size?: number; className?: string }>;
}) {
  return (
    <Link to={link} className="block group">
      <Widget accent={accent}>
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="fg-eyebrow">{title}</p>
            <p className="mt-2 text-3xl sm:text-4xl font-black tracking-tighter text-white counter-text">
              {typeof value === "number" ? (
                <AnimatedCounter target={value} />
              ) : (
                value
              )}
            </p>
            <p className="mt-1 text-[11px] text-white/45">{hint}</p>
          </div>
          <AccentDot accent={accent} size="lg">
            <Icon size={20} className="" />
          </AccentDot>
        </div>
        <div
          className="inline-flex items-center gap-1 text-[11px] font-bold mt-3 opacity-0 group-hover:opacity-100 transition-opacity"
          style={{ color: accentHex(accent) }}
        >
          Open <ChevronRight size={11} />
        </div>
      </Widget>
    </Link>
  );
}

const ACTION_META: Record<AuditLog["action"], { label: string; accent: WidgetAccent; icon: React.ComponentType<{ size?: number; className?: string }> }> = {
  token_issued: { label: "Token issued", accent: "yellow", icon: KeyRound },
  key_fetched:  { label: "Key fetched",  accent: "green",  icon: Activity },
  auth_failed:  { label: "Auth failed",  accent: "red",    icon: AlertTriangle },
  segment_404:  { label: "Segment 404",  accent: "orange", icon: Clock },
};

export function DashboardPage() {
  const stats = useQuery<Stats>({
    queryKey: ["admin", "stats"],
    queryFn: async () => (await api.get("/api/levels/admin/stats")).data,
    refetchOnWindowFocus: false,
  });
  const audit = useQuery<AuditLog[]>({
    queryKey: ["admin", "audit", "recent"],
    queryFn: async () => {
      try {
        return (await api.get("/api/admin/audit-logs?limit=8")).data;
      } catch {
        return [];
      }
    },
    refetchOnWindowFocus: false,
  });

  useScrollRevealAll(".reveal");

  const isRefreshing = stats.isFetching || audit.isFetching;
  const handleRefresh = () => {
    stats.refetch();
    audit.refetch();
  };

  return (
    <AdminShell>
      <Bento>
        {/* ── Hero ──────────────────────────────────────────────── */}
        <PageHero
          accent="yellow"
          eyebrow={
            <span className="inline-flex items-center gap-1.5">
              <Zap size={11} /> Command center
            </span>
          }
          title="Platform overview"
          description={
            <>
              A1 → C1 curriculum ·{" "}
              <span className="text-white font-bold">
                {stats.data?.users.toLocaleString() ?? "…"}
              </span>{" "}
              active learners
              {(stats.data?.lessonsEncodingPending ?? 0) > 0 && (
                <Pill accent="orange">
                  <Clock size={10} />
                  {stats.data?.lessonsEncodingPending} encoding
                </Pill>
              )}
            </>
          }
          aside={
            <button
              type="button"
              onClick={handleRefresh}
              className="fg-cta-secondary"
            >
              <RefreshCw
                size={13}
                className={clsx(isRefreshing && "animate-spin")}
              />
              Refresh
            </button>
          }
        />

        {/* ── KPI tiles (4 bento) ──────────────────────────────── */}
        <StatTile
          title="Learners"
          value={stats.data?.users ?? "—"}
          hint="Active accounts"
          link="/learners"
          accent="blue"
          icon={Users}
        />
        <StatTile
          title="Levels"
          value={stats.data?.levels ?? "—"}
          hint="A1 → C1 curriculum"
          link="/levels"
          accent="yellow"
          icon={BookOpenCheck}
        />
        <StatTile
          title="Lessons"
          value={stats.data?.lessons ?? "—"}
          hint="Video + exercises"
          link="/lessons"
          accent="green"
          icon={GraduationCap}
        />
        <StatTile
          title="Exercises"
          value={stats.data?.exercises ?? "—"}
          hint="5 types supported"
          link="/exercises"
          accent="purple"
          icon={ListChecks}
        />

        {/* ── Encoding alert (only when needed) ──────────────────── */}
        {(stats.data?.lessonsEncodingPending ?? 0) > 0 && (
          <Widget accent="orange" fullWidth hero>
            <div className="flex items-center gap-4">
              <AccentDot accent="orange" size="lg">
                <AlertTriangle size={20} />
              </AccentDot>
              <div className="flex-1 min-w-0">
                <h3 className="text-base font-bold text-[#FF9F0A]">
                  {stats.data!.lessonsEncodingPending} lesson
                  {stats.data!.lessonsEncodingPending === 1 ? "" : "s"} need attention
                </h3>
                <p className="mt-1 text-[13px] text-white/55">
                  Pending / failed encodes. Open{" "}
                  <Link to="/lessons" className="text-[#FFD60A] hover:underline font-bold">
                    Lessons
                  </Link>{" "}
                  to retry.
                </p>
              </div>
              <Link to="/lessons" className="fg-cta-primary shrink-0">
                Fix now <ArrowRight size={14} />
              </Link>
            </div>
          </Widget>
        )}

        {/* ── Quick actions bento (6 actions, 3-col) ────────────── */}
        <Widget accent="yellow" fullWidth hero
          eyebrow="Shortcuts"
          title="Quick actions"
          subtitle="Common tasks — one click away."
        >
          <Bento>
            {QUICK_ACTIONS.map((qa, idx) => {
              const Icon = qa.icon;
              return (
                <motion.div
                  key={qa.href}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: idx * 0.04 }}
                >
                  <Link
                    to={qa.href}
                    className="group flex flex-col gap-2 min-h-[120px]"
                  >
                    <AccentDot accent={qa.accent} size="lg">
                      <Icon size={20} />
                    </AccentDot>
                    <p className="text-base font-bold tracking-tight text-white mt-1">
                      {qa.label}
                    </p>
                    <p className="text-[12px] text-white/55 leading-relaxed flex-1">
                      {qa.description}
                    </p>
                    <span
                      className="inline-flex items-center gap-1 text-[11px] font-bold mt-1"
                      style={{ color: accentHex(qa.accent) }}
                    >
                      Open <ChevronRight size={11} className="group-hover:translate-x-0.5 transition-transform" />
                    </span>
                  </Link>
                </motion.div>
              );
            })}
          </Bento>
        </Widget>

        {/* ── Recent activity (fullWidth) ────────────────────────── */}
        <Widget
          accent="blue"
          fullWidth
          hero
          eyebrow="Live signal"
          title="Recent activity"
          subtitle="Last 8 audit events from video_audit_logs. Live signal for account-sharing farms and token misuse."
          trailing={
            <button
              type="button"
              onClick={() => audit.refetch()}
              className="fg-cta-secondary"
            >
              <RefreshCw
                size={12}
                className={clsx(audit.isFetching && "animate-spin")}
              />
              Refresh
            </button>
          }
        >
          {audit.isLoading ? (
            <ul className="space-y-1.5">
              {Array.from({ length: 4 }).map((_, i) => (
                <li key={i} className="h-9 rounded-md bg-white/5 fg-skeleton" />
              ))}
            </ul>
          ) : !audit.data || audit.data.length === 0 ? (
            <div className="text-center py-8">
              <TrendingUp className="w-8 h-8 mx-auto text-white/40 mb-2" />
              <p className="text-xs text-white/55">
                No audit events yet. Activity will show up here once learners
                start streaming.
              </p>
            </div>
          ) : (
            <ul className="space-y-1.5 max-h-80 overflow-y-auto pr-1">
              {audit.data.slice(0, 8).map((a, idx) => {
                const meta = ACTION_META[a.action];
                const Icon = meta.icon;
                return (
                  <motion.li
                    key={a.id}
                    initial={{ opacity: 0, x: -4 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.25, delay: idx * 0.04 }}
                    className="flex items-center gap-3 rounded-lg bg-white/[0.02] border border-[rgba(255,255,255,0.04)] px-3 py-2"
                  >
                    <AccentDot accent={meta.accent} size="sm">
                      <Icon size={12} />
                    </AccentDot>
                    <span className="text-[12px] font-mono text-white flex-1 truncate">
                      {meta.label}
                    </span>
                    <span className="text-[10px] font-mono text-white/45 truncate max-w-[8rem]">
                      {a.userId?.slice(0, 8) ?? a.ipHash?.slice(0, 8) ?? "—"}
                    </span>
                    <span className="text-[10px] font-mono text-white/45">
                      {new Date(a.createdAt).toLocaleTimeString()}
                    </span>
                  </motion.li>
                );
              })}
            </ul>
          )}
        </Widget>

        {/* ── Quick start guide (fullWidth) ──────────────────────── */}
        <Widget accent="neutral" fullWidth hero
          eyebrow="Onboarding"
          title="Quick start"
          subtitle="The four steps to ship a new CEFR level end-to-end."
        >
          <ol className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
            <QuickStep
              n={1}
              accent="yellow"
              title="Create the level"
              body={
                <>
                  Open{" "}
                  <Link to="/levels" className="text-[#FFD60A] hover:underline font-bold">
                    Levels
                  </Link>{" "}
                  and add your first level (e.g. <code className="rounded bg-white/5 px-1 text-[10px]">A1.1</code>).
                </>
              }
            />
            <QuickStep
              n={2}
              accent="green"
              title="Add a lesson"
              body={
                <>
                  Inside the new level, hop to{" "}
                  <Link to="/lessons" className="text-[#34C759] hover:underline font-bold">
                    Lessons
                  </Link>{" "}
                  and create the first one.
                </>
              }
            />
            <QuickStep
              n={3}
              accent="blue"
              title="Upload MP4"
              body="The backend encodes three HLS ladders; stream via the signed URL within a minute."
            />
            <QuickStep
              n={4}
              accent="purple"
              title="Mark as a new drop"
              body={
                <>
                  Toggle <em>New drop</em> on the level to surface it in the landing page's
                  <em> Just dropped </em> banner.
                </>
              }
            />
          </ol>
        </Widget>
      </Bento>
    </AdminShell>
  );
}

function QuickStep({
  n,
  accent,
  title,
  body,
}: {
  n: number;
  accent: WidgetAccent;
  title: string;
  body: React.ReactNode;
}) {
  return (
    <li className="flex items-start gap-3 rounded-xl bg-white/[0.02] border border-white/5 p-3">
      <AccentDot accent={accent} size="md">
        <span className="text-sm font-black">{n}</span>
      </AccentDot>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-bold text-white">{title}</p>
        <p className="mt-0.5 text-[12px] text-white/55 leading-relaxed">{body}</p>
      </div>
    </li>
  );
}